import { RefObject, useEffect, useMemo, useRef } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useLayoutMainContext, useReportId } from "@ryde/redux";
import {
  reportsTableColumnsVisibilityAF,
  tableElementRefReportsAtom,
} from "@ryde/shared";

interface IUsePagingStickyEmulationReportsArguments {
  pagingRef: RefObject<HTMLDivElement>;
}

export const usePagingStickyEmulationReports = ({
  pagingRef,
}: IUsePagingStickyEmulationReportsArguments): null => {
  const { reportId } = useReportId();
  const columnVisibility = useRecoilValue(
    reportsTableColumnsVisibilityAF(reportId),
  );
  const { mainElement } = useLayoutMainContext();

  const visibleColumnNumber = useMemo(
    () => Object.values(columnVisibility).filter(Boolean).length,
    [columnVisibility],
  );
  const relativeElement = mainElement;
  const prevVisibleColumnNumber = useRef<number>(visibleColumnNumber);
  const table = useRecoilValue(tableElementRefReportsAtom);
  const scrollListener = useEvent((): void => {
    window.requestAnimationFrame(() => {
      if (!pagingRef.current || !relativeElement) {
        return;
      }

      pagingRef.current.style.transform = `translateX(${relativeElement.scrollLeft}px)`;
    });
  });

  useEffect(() => {
    if (!pagingRef.current || !relativeElement) {
      return;
    }

    relativeElement.addEventListener("scroll", scrollListener);

    return () => {
      relativeElement.removeEventListener("scroll", scrollListener);
    };
  }, [relativeElement, pagingRef, scrollListener]);
  useEffect(() => {
    if (!pagingRef.current || !relativeElement || !table) {
      return;
    }

    const isVisibleColumnNumberDecreased =
      visibleColumnNumber - prevVisibleColumnNumber.current < 0;

    const isOverScroll =
      relativeElement.scrollLeft + relativeElement.clientWidth >=
      table.clientWidth;

    if (isVisibleColumnNumberDecreased && isOverScroll) {
      pagingRef.current.style.transform = `translateX(${0}px)`;
    }

    prevVisibleColumnNumber.current = visibleColumnNumber;
  }, [relativeElement, pagingRef, table, visibleColumnNumber]);

  return null;
};
